import { AuthState } from 'store/types';
import {
    BindableClassListResponse,
    BindableDataChoiceResponse,
    CmsContentListResponse,
    FileDeleteResponse,
    FileListResponse,
    FileMetadataResponse,
    FileRemovalProbeResponse,
    PageDetailResponse,
    PageListResponse,
    PagePreviewListResponse,
    PagePreviewResponse,
    PageStateBody,
    TranslateRequestObject,
    UploadedFileResponse,
} from './types/cms';
import { PaginationTypeResponse, RemoveResponse } from './types';
import { FormValues } from '../components/form/types';
import { ApiFactoryClassType } from './factory';

const buildXmlHttpRequest = (
    url: URL | string,
    data: any,
    session: AuthState,
    onProgress?: (progress: number) => void,
): Promise<UploadedFileResponse> => {
    const xhr = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
        if (onProgress) {
            xhr.upload.addEventListener('progress', event => {
                if (event.lengthComputable) {
                    onProgress(event.loaded / event.total);
                }
            });
        }
        xhr.addEventListener('error', () => reject(xhr.status));
        xhr.addEventListener('loadend', () => {
            if (xhr.status < 400) {
                resolve(JSON.parse(xhr.response));
            } else {
                reject(xhr.status);
            }
        });
        xhr.open('POST', url, true);
        xhr.setRequestHeader(
            'Authorization',
            `Bearer ${session.auth?.access_token}`,
        );
        xhr.send(data);
    });
};

const knownStatus = [200, 201, 400, 401, 403, 404, 500];

const cms = (factory: ApiFactoryClassType) => ({
    getPools: async (
        limit: number = 15,
        offset: number = 0,
    ): Promise<PaginationTypeResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/pool/list?limit=${limit}&offset=${offset}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getFiles: async (
        limit: number = 15,
        offset: number = 0,
        poolId?: string,
        accept?: string,
        contentName?: string,
    ): Promise<PaginationTypeResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            let path = `/api/cms/file/list?limit=${limit}&offset=${offset}`;
            if (poolId) {
                path += `&pool=${poolId}`;
            }
            if (accept) {
                path += `&accept=${accept}`;
            }
            if (contentName) {
                path += `&content=${contentName}`;
            }
            const response = await fetch(new URL(path, factory.getApiHost()), {
                method: 'GET',
                headers: {
                    Authorization: 'Bearer ' + session.auth.access_token,
                },
                mode: 'cors',
            });

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json().catch(() => null);
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getFile: async (fileId: string): Promise<FileMetadataResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/file/${fileId}`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getVersionsByFile: async (
        fileId: string,
    ): Promise<FileListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/file/${fileId}/versions`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    uploadFile: async (
        file: File,
        poolId?: string,
        contentName?: string,
        onProgress?: (progress: number) => void,
    ): Promise<UploadedFileResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const data = new FormData();
            data.append('file', file);

            let path = '/api/cms/file/new';
            if (poolId) {
                path += `?pool=${poolId}`;
                if (contentName) {
                    path += `&contentName=${contentName}`;
                }
            } else {
                if (contentName) {
                    path += `?contentName=${contentName}`;
                }
            }

            return await buildXmlHttpRequest(
                new URL(path, factory.getApiHost()),
                data,
                session,
                onProgress,
            );
        } catch (status: any) {
            if (!knownStatus.includes(status)) {
                factory.onNetworkError();
            } else {
                return Promise.reject({ code: status });
            }
            return null;
        }
    },
    updateFile: async (
        fileId: string,
        file: File,
        fileDates?: { startDate: string; endDate: string },
        onProgress?: (progress: number) => void,
    ): Promise<UploadedFileResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const data = new FormData();
            data.append('file', file);

            return await buildXmlHttpRequest(
                new URL(
                    `/api/cms/file/${fileId}/update${fileDates?.startDate || fileDates?.endDate ? `?startDate=${fileDates.startDate}&endDate=${fileDates.endDate}` : ``}`,
                    factory.getApiHost(),
                ),
                data,
                session,
                onProgress,
            );
        } catch (status: any) {
            if (!knownStatus.includes(status)) {
                factory.onNetworkError();
            } else {
                return Promise.reject({ code: status });
            }
            return null;
        }
    },
    toggleFile: async (
        id: string,
        stateBody: { enabled: boolean },
    ): Promise<UploadedFileResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/file/${id}/toggle`, factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(stateBody),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    editFile: async (
        id: string,
        data: any,
    ): Promise<UploadedFileResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/file/${id}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify(data),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    removalProbe: async (
        fileId: string,
    ): Promise<FileRemovalProbeResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/file/${fileId}/removal-probe`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deleteFileByFileId: async (
        fileId: string,
        versionId?: string,
    ): Promise<FileDeleteResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        let path = `/api/cms/file/${fileId}`;
        if (versionId) {
            path += `?version=${versionId}`;
        }

        try {
            const response = await fetch(new URL(path, factory.getApiHost()), {
                method: 'DELETE',
                headers: {
                    Authorization: 'Bearer ' + session.auth.access_token,
                },
                mode: 'cors',
            });

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPageList: async (
        locale: string,
        parent: string | null = null,
    ): Promise<PageListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            let url = `/api/cms/page/list?l=${locale}`;
            if (parent) {
                url += `&p=${parent}`;
            }

            const response = await fetch(new URL(url, factory.getApiHost()), {
                method: 'GET',
                headers: {
                    Authorization: 'Bearer ' + session.auth.access_token,
                },
                mode: 'cors',
            });

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPageById: async (
        id: string,
        date: string | undefined = undefined,
    ): Promise<PageDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/page/${id}`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getBindableClassList:
        async (): Promise<BindableClassListResponse | null> => {
            const session = factory.getSession();
            if (!session || !session.auth || !session.auth.access_token) {
                return Promise.reject('no active session');
            }
            try {
                const response = await fetch(
                    new URL(
                        `/api/cms/page/list-bindable-classes`,
                        factory.getApiHost(),
                    ),
                    {
                        method: 'GET',
                        headers: {
                            Authorization:
                                'Bearer ' + session.auth.access_token,
                        },
                        mode: 'cors',
                    },
                );

                if (!response.ok) {
                    factory.onError(response);
                }

                return response.json();
            } catch (e) {
                factory.onNetworkError();
                return null;
            }
        },
    getConnectItems: async (
        type: string,
    ): Promise<CmsContentListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/content/${type}`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPageDataChoiceList: async (
        pageId: string,
        query: string,
        limit: number = 15,
        offset: number = 0,
    ): Promise<BindableDataChoiceResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/page/${pageId}/list-choices?query=${query}&limit=${limit}&offset=${offset}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    updatePageState: async (
        id: string,
        stateBody: PageStateBody,
    ): Promise<PageDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/page/${id}/state`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify(stateBody),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createPage: async (
        pageObj: FormValues,
    ): Promise<PageDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/cms/page/create', factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(pageObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    updatePage: async (
        id: string,
        pageObj: FormValues,
    ): Promise<PageDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/page/${id}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify(pageObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deletePage: async (id: string): Promise<RemoveResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/page/${id}`, factory.getApiHost()),
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    copyPage: async (
        pageId: string,
        pageObj: FormValues = {},
    ): Promise<PageDetailResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/page/${pageId}/copy`, factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(pageObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getPagePreviewTokenList: async (
        pageId: string,
    ): Promise<PagePreviewListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/page/${pageId}/preview-tokens`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createPagePreview: async (
        pageId: string,
        previewObj: FormValues = {},
    ): Promise<PagePreviewResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/page/${pageId}/create-preview`,
                    factory.getApiHost(),
                ),
                {
                    method: 'POST',
                    body: JSON.stringify(previewObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deletePagePreview: async (
        previewToken: string,
    ): Promise<PagePreviewResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/cms/page/delete-preview/${previewToken}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getSupportedLanguages: async () => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/translate/supported`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Accept-Language': factory.getRequestLocale(),
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    translateAnyText: async (translateObj: TranslateRequestObject) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/cms/translate/text`, factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(translateObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
});

export default cms;
